import {
  Component,
  computed,
  OnInit,
  signal,
  AfterViewInit,
  effect,
  inject,
  HostListener,
  OnDestroy,
  untracked,
  input,
  ChangeDetectorRef,
  ChangeDetectionStrategy
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  CdkDragDrop,
  DragDropModule,
} from '@angular/cdk/drag-drop';
import { catchError } from 'rxjs/operators';
import { of } from 'rxjs';
import {
  LineAreaChartComponent,
  ILineAreaSeries,
} from '../../../shared/components/charts/line-area-chart/line-area-chart.component';
import { IconComponent } from '../../../shared/icons/icon.component';
import { ApiService } from '../../../core/services/api.service';
import { CommonService } from '../../../core/services/common.service';
import { AiBotService } from '../../../core/services/ai-bot.service';
import { ProductConfigService } from '../../../core/services/product-config.service';
import { IconComponentV2 } from '../../../shared/components/icon-component/icon.component';
import { IndividualToasterComponent } from '../../../shared/components/loader/loader.component';
import { ThemeService } from '../../../core/services/theme.service';
import { Subscription } from 'rxjs';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import weekOfYear from 'dayjs/plugin/weekOfYear';
import isoWeek from 'dayjs/plugin/isoWeek';
import * as d3 from 'd3';
import { ToggleBtnsComponent } from '../../../shared/components/toggle-btns/toggle-btns.component';
import { DatePickerComponent, DateRangePickerOutput } from '../../../shared/components/date-picker/date-picker.component';
import { TableHeader, ScrollTableComponent } from '../../../shared/components/tables/scrolled-table/scroll-table.component';
// import { UnsupportedProductComponent } from '../../../shared/components/unsupported-product/unsupported-product.component';
import { AiHeadlinesComponent } from '../../../shared/components/ai-headlines/ai-headlines.component';
import { environment } from '../../../../environments/environment';
import {
  DashboardPageKey,
  OverdueCronSectionId,
  OverdueCronSectionDef,
} from '../../../core/enums/dashboard-cards.enum';
import { CustomDashboardPinService } from '../../../core/services/custom-dashboard-pin.service';
import { DashboardLayoutService } from '../../../core/services/dashboard-layout.service';
import { CustomizeLayoutBatchService } from '../../../core/services/customize-layout-batch.service';
import { ToasterService } from '../../../core/services/toaster.service';
import {
  createEmbeddedSectionFilter,
  findCatalogPageId,
  resolveActiveCatalogProductId,
  stripProductPrefix,
} from '../../../shared/components/dashboard/dashboard-embed.helpers';
import {
  applyLayoutEditorAutoWidthUpdates,
  applyLayoutEditorCardDrop,
  computeLayoutEditorAutoWidthUpdates,
  resolveLayoutEditorGroupDropBounds,
} from '../../../shared/components/dashboard/dashboard-layout-editor.helpers';
import { LayoutEditorOverlayDirective } from '../../../shared/components/dashboard/layout-editor-overlay.directive';

dayjs.extend(utc);
dayjs.extend(weekOfYear);
dayjs.extend(isoWeek);

export type ChartTimeUnit = 'week' | 'month' | 'quarter' | 'year';

interface PaymentTrendItem {
  date: string;
  successPayments: number;
  failedPayments: number;
  totalPayments: number;
}

interface PaymentAmountItem {
  date: string;
  successAmount: number;
  failedAmount: number;
  totalAmount: number;
}

interface PieChartData {
  label: string;
  value: number;
}

/** Persisted layout state per section (visibility + width + order). */
interface OverdueCronSectionLayoutItem {
  id: OverdueCronSectionId;
  visible: boolean;
  /** 4 | 6 | 8 | 12 — proportional width inside the 12-col dashboard grid. */
  colSpan: number;
  order: number;
}

/** Draft row used by the inline Customize Layout editor. */
interface OverdueCronLayoutEditorItem extends OverdueCronSectionLayoutItem {
  title: string;
  /** Visual grouping in the live page (overview vs analytics). The
   *  editor's drag-drop handler clamps target indices so a section
   *  never crosses into another group — this preserves the page's
   *  existing top/bottom split (the static date picker control sits
   *  between groups and is not part of the customizable layout). */
  group: 'overview' | 'analytics';
}

@Component({
  selector: 'app-overdue-cron',
  standalone: true,
  imports: [
    CommonModule,
    LineAreaChartComponent,
    IconComponent,
    IndividualToasterComponent,
    ToggleBtnsComponent,
    DatePickerComponent,
    ScrollTableComponent,
    // UnsupportedProductComponent,
    AiHeadlinesComponent,
    IconComponentV2,
    DragDropModule,
    LayoutEditorOverlayDirective,
  ],
  templateUrl: './overdue-cron.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './overdue-cron.component.scss',
})
export class OverdueCronComponent implements OnInit, AfterViewInit, OnDestroy {
  private apiService = inject(ApiService);
  private commonService = inject(CommonService);
  private aiBotService = inject(AiBotService);
  private productConfigService = inject(ProductConfigService);
  private themeService = inject(ThemeService);
  private customDashboardPin = inject(CustomDashboardPinService);
  private dashboardLayoutService = inject(DashboardLayoutService);
  private _customizeLayoutBatch = inject(CustomizeLayoutBatchService);
  private toaster = inject(ToasterService);
  private cdr = inject(ChangeDetectorRef);
  private themeSubscription?: Subscription;
  private resolveAgentSubscription?: Subscription;
  private resizeTimeout: any;
  private productConfig = computed(() => {
    // Rely on the signal which is updated in initializeComponentData
    const productId = this.apiService.getSelectedProductId();
    const productName = this.productConfigService.getProductName(productId || 'denefits');
    return this.productConfigService.getProductConfig(productName);
  });

  private productServerName = computed(() => this.productConfig().productServerName);

  isUnsupportedProduct = computed(() => {
    const serverName = this.productServerName();
    // return serverName === 'finance-mutual' || serverName === 'recuvery';
    return false;
  });

  isDenefits = computed(() =>
    this.productConfigService.isDenefitsProduct(this.apiService.selectedProductId())
  );

  currentTheme: 'light' | 'dark' = 'dark';
  isDarkTheme = signal(false);

  selectedStartDate = signal<string>(dayjs().subtract(3, 'month').startOf('day').toISOString());
  selectedEndDate = signal<string>(dayjs().endOf('day').toISOString());
  canCallApi = signal<boolean>(false);

  // Inputs that mirror the daily-cron pattern so this component can also
  // be embedded in My Dashboard later (currently it renders standalone,
  // so `mixedMode` defaults to false and the customizable grid behaves
  // exactly like before for any caller that doesn't pass these inputs).
  readonly mixedMode = input<boolean>(false);
  readonly visibleWidgetKeys = input<string[] | null>(null);
  readonly catalogPageId = input<number | null>(null);

  readonly effectiveCatalogPageId = signal<number | null>(null);

  readonly PAGE_KEY = DashboardPageKey.OVERDUE_CRON_PAYMENTS;
  readonly SectionId = OverdueCronSectionId;
  readonly SECTIONS = OverdueCronSectionDef;

  /**
   * Sections rendered in the top "overview" grid (above the static date
   * picker). The remaining sections render in the bottom "analytics" grid.
   * The customize layout popup edits all sections in a single list, but
   * the drop handler clamps target indices to keep each section in its
   * original group — this preserves the page's existing flow where the
   * date picker control visually separates "most recent" stats from
   * date-range-driven analytics.
   */
  private readonly OVERVIEW_SECTION_IDS: ReadonlySet<OverdueCronSectionId> =
    new Set<OverdueCronSectionId>([
      OverdueCronSectionId.RECENT_ATTEMPT_OVERVIEW,
    ]);

  private getSectionGroup(id: OverdueCronSectionId): 'overview' | 'analytics' {
    return this.OVERVIEW_SECTION_IDS.has(id) ? 'overview' : 'analytics';
  }

  private formatDateForApi(dateStr: string): string {
    return dayjs(dateStr).format('YYYY-MM-DD');
  }

  constructor() {
    // Create effects in constructor (injection context)
    effect(() => {
      const methodData = this.paymentMethodDistribution();
      if (methodData.length > 0) {
        // Use requestAnimationFrame for better timing
        requestAnimationFrame(() => {
          this.renderPaymentMethodPieChart();
        });
      }
    });

    effect(() => {
      const statusData = this.paymentStatusDistribution();
      if (statusData.length > 0) {
        requestAnimationFrame(() => {
          this.renderPaymentStatusPieChart();
        });
      }
    });

    // Reactive data fetching based on selected product
    effect(() => {
      const productId = this.apiService.selectedProductId();
      if (productId) {
        untracked(() => {
          if (!this.isUnsupportedProduct()) {
            if (
              this.showSection(OverdueCronSectionId.ANALYTICS_SUMMARY) ||
              this.showSection(OverdueCronSectionId.VOLUME_TRENDS) ||
              this.showSection(OverdueCronSectionId.TRANSACTION_AMOUNTS) ||
              this.showSection(OverdueCronSectionId.STATUS_DISTRIBUTION) ||
              this.showSection(OverdueCronSectionId.METHOD_DISTRIBUTION) ||
              this.showSection(OverdueCronSectionId.REVENUE_DISTRIBUTION) ||
              this.showSection(OverdueCronSectionId.SUCCESS_RATE_TREND) ||
              this.showSection(OverdueCronSectionId.DETAILED_STATISTICS)
            ) {
              this.customRangeData({
                start_date: this.formatDateForApi(this.selectedStartDate()),
                end_date: this.formatDateForApi(this.selectedEndDate()),
              } as any);
            }
            if (this.showSection(OverdueCronSectionId.RECENT_ATTEMPT_OVERVIEW)) {
              this.mostRecentTotal();
            }
            this.canCallApi.set(true);
          }
        });
      }
    });

    // Resolve catalog page id for Overdue Cron Payments. Mirrors the same
    // priority used by daily-cron / campaign-ads-stats: parent-supplied id
    // wins, else resolve from the catalog scoped to the active product.
    effect(() => {
      const fromParent = this.catalogPageId();
      if (fromParent != null) {
        untracked(() => this.effectiveCatalogPageId.set(fromParent));
        return;
      }
      const productId = this.apiService.selectedProductId();
      if (!productId) return;
      untracked(() => {
        this.dashboardLayoutService.getCatalogProducts().subscribe((catalog) => {
          const activeProductPid = resolveActiveCatalogProductId(this.apiService);
          const pageId = findCatalogPageId(catalog as any[], this.PAGE_KEY, activeProductPid);
          if (pageId != null) this.effectiveCatalogPageId.set(pageId);
        });
      });
    });

    // Hydrate already-pinned sections so the "Added" badge is accurate
    // after a page refresh. No-op when the catalog hasn't registered the
    // Overdue Cron Payments page yet.
    effect(() => {
      if (this.mixedMode()) return;
      const pageId = this.effectiveCatalogPageId();
      if (pageId == null) return;
      untracked(() => {
        this.customDashboardPin.hydratePinnedWidgetKeys(pageId).subscribe((keys) => {
          const pinned = new Set<OverdueCronSectionId>();
          keys.forEach((k) => {
            if (Object.values(OverdueCronSectionId).includes(k as OverdueCronSectionId)) {
              pinned.add(k as OverdueCronSectionId);
            }
          });
          this.pinnedSections.set(pinned);
        });
      });
    });

    // Pre-resolve catalog `widgets.id` per section so per-click pins use
    // the fast `toggleLayoutItem` path (one POST/PATCH per click). When
    // the catalog hasn't registered Overdue Cron yet, this map stays empty
    // and `addToCustomDashboard` gracefully falls back to the slower
    // `toggleSection` path (which itself silently warns if no catalog
    // entry exists, so nothing crashes either way).
    effect(() => {
      if (this.mixedMode()) return;
      const pageId = this.effectiveCatalogPageId();
      if (pageId == null) return;
      const productId = resolveActiveCatalogProductId(this.apiService);
      untracked(() => {
        this.customDashboardPin
          .resolveCatalogWidgetIdsForPage(pageId, productId)
          .subscribe((widgetIdMap) => {
            const next = new Map<OverdueCronSectionId, number>();
            for (const sec of Object.values(OverdueCronSectionId)) {
              const id = widgetIdMap.get(sec);
              if (id != null) next.set(sec, id);
            }
            this.pinnableWidgetIds.set(next);
          });
      });
    });
  }

  @HostListener('window:resize')
  onResize() {
    // Debounce resize events
    clearTimeout(this.resizeTimeout);
    this.resizeTimeout = setTimeout(() => {
      this.renderPaymentMethodPieChart();
      this.renderPaymentStatusPieChart();
    }, 250);
  }

  mostRecentStats = {
    firstLoad: true,
    loading: false,
    date: '',
    data: {
      total_attempts: 0,
      successful: {
        total: 0,
        amount: 0,
        rate: 0,
      },
      failed: {
        total: 0,
        amount: 0,
        rate: 0,
      },
      pending: {
        total: 0,
        amount: 0,
      },
      card_payments: {
        total: 0,
        successful: {
          total: 0,
          amount: 0,
        },
        failed: {
          total: 0,
          amount: 0,
        },
      },
      ach_payments: {
        total: 0,
        successful: {
          total: 0,
          amount: 0,
        },
        failed: {
          total: 0,
          amount: 0,
        },
        pending: {
          total: 0,
          amount: 0,
        },
      },
    },
  };



  ngOnInit(): void {
    this.commonService.activeChatContextKey.set('CRON_PAYMENTS');
    this.setAiSuggestions();

    const productId = this.apiService.getSelectedProductId() || sessionStorage.getItem('selected_product_id');
    if (productId && this.commonService.isPanelOpen()) {
      const productName = this.productConfigService.getProductName(productId).toLowerCase();
      if (productName) {
        this.resolveAiAgent(productName);
      }
    }

    // Subscribe to theme changes
    this.themeSubscription = this.themeService.theme$.subscribe((theme) => {
      this.currentTheme = theme;
      this.isDarkTheme.set(theme === 'dark');
      // Redraw charts on theme change
      setTimeout(() => {
        this.renderPaymentMethodPieChart();
        this.renderPaymentStatusPieChart();
      }, 100);
    });

    this._initLayout();
  }

  ngOnDestroy(): void {
    this.resolveAgentSubscription?.unsubscribe();
    if (this.themeSubscription) {
      this.themeSubscription.unsubscribe();
    }
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
    }
    this.commonService.activeAgentId.set(null);
    this.commonService.isPanelOpen.set(false);
    this.commonService.activeChatContextKey.set(null);
  }

  openAiAssistant(): void {
    const productId = this.apiService.getSelectedProductId() || sessionStorage.getItem('selected_product_id');
    if (!productId) return;

    const productName = this.productConfigService.getProductName(productId).toLowerCase();
    this.commonService.chatType.set('CRON_PAYMENTS');
    this.commonService.isPanelOpen.set(true);

    const contextKey = this.commonService.activeChatContextKey();
    if (contextKey) {
      const savedState = this.commonService.getChatState(contextKey);
      if (!savedState) {
        this.setAiSuggestions();
      }
    }

    this.resolveAiAgent(productName);
  }

  private setAiSuggestions(): void {
    this.commonService.aiSuggestions.set([
      'Show Most Recent Payment Attempt.',
      'What was the success rate during the last 12 months?',
      'Show payment volume trends for the last 90 days.',
      'Give me revenue distribution from Jan 2023 to Dec 2025.',
    ]);
  }

  private resolveAiAgent(productName: string): void {
    this.resolveAgentSubscription?.unsubscribe();

    this.resolveAgentSubscription = this.aiBotService
      .resolveAgent(productName, 'cron_payments', 'ask_prism_ai')
      .subscribe({
        next: (res: any) => {
          const agentId = res?.agent_id || res?.data?.agent_id;
          this.commonService.activeAgentId.set(agentId ?? null);
        },
        error: () => {
          this.commonService.activeAgentId.set(null);
        },
      });
  }

  ngAfterViewInit(): void {
    this.currentTheme = this.themeService.getTheme();
  }

  mostRecentTotal() {
    this.mostRecentStats.loading = true;
    this.cdr.markForCheck();
    const url = this.productConfig().overdue_cron_latest_sub_url || 'subadmin/get_latest_friday_cron_data';

    this.apiService
      .post(
        url,
        {},
        {
          addAccessToken: false,
          addSecurityHeaders: false,
          productServerName: this.productServerName(),
          showLoader: this.mostRecentStats.firstLoad,
        }
      )
      .subscribe({
        next: (response: any) => {
          this.mostRecentStats.loading = false;
          this.mostRecentStats.firstLoad = false;
          const data = response.data;
          this.mostRecentStats.date = data[0].DATE;
          this.mostRecentStats.data = {
            total_attempts: data[0].total_payments,
            successful: {
              total:
                data[0].success_card_payments + data[0].success_ach_payments,
              amount:
                data[0].success_card_payments_amount +
                data[0].success_ach_payments_amount,
              rate: Number(
                (
                  ((data[0].success_card_payments +
                    data[0].success_ach_payments) /
                    data[0].total_payments) *
                  100
                ).toFixed(2)
              ),
            },
            failed: {
              total: data[0].failed_card_payments + data[0].failed_ach_payments,
              amount:
                data[0].failed_card_payments_amount +
                data[0].failed_ach_payments_amount,
              rate: Number(
                (
                  ((data[0].failed_card_payments +
                    data[0].failed_ach_payments) /
                    data[0].total_payments) *
                  100
                ).toFixed(2)
              ),
            },
            pending: {
              total: data[0].pending_ach_payments,
              amount: data[0].pending_ach_payments_amount,
            },
            card_payments: {
              total: data[0].card_payments,
              successful: {
                total: data[0].success_card_payments,
                amount: data[0].success_card_payments_amount,
              },
              failed: {
                total: data[0].failed_card_payments,
                amount: data[0].failed_card_payments_amount,
              },
            },
            ach_payments: {
              total: data[0].ach_payments,
              successful: {
                total: data[0].success_ach_payments,
                amount: data[0].success_ach_payments_amount,
              },
              failed: {
                total: data[0].failed_ach_payments,
                amount: data[0].failed_ach_payments_amount,
              },
              pending: {
                total: data[0].pending_ach_payments,
                amount: data[0].pending_ach_payments_amount,
              },
            },
          };
          this.cdr.markForCheck();
        },
        error: (error: any) => {
          console.error(JSON.stringify(error, null, 2));
          this.mostRecentStats.loading = false;
          this.cdr.markForCheck();
        },
      });
  }

  // Chart time unit controls (driven by DatePicker, restricted)
  chartUnit = signal<ChartTimeUnit>('week');

  onDateRangeChange(event: DateRangePickerOutput) {
    const prevStart = this.selectedStartDate();
    const prevEnd = this.selectedEndDate();
    const sameRange =
      dayjs(prevStart).isSame(dayjs(event.start_date), 'day') &&
      dayjs(prevEnd).isSame(dayjs(event.end_date), 'day');
    const hasData =
      (this.paymentVolumeTrend()?.length ?? 0) > 0 ||
      (this.paymentAmountTrend()?.length ?? 0) > 0 ||
      (this.successRateTrend()?.length ?? 0) > 0;

    this.selectedStartDate.set(event.start_date);
    this.selectedEndDate.set(event.end_date);

    const start = dayjs(event.start_date);
    const end = dayjs(event.end_date);
    const diffDays = end.diff(start, 'day');
    const halfYearDays = 180;
    const oneYearDays = 365;

    let incomingUnit = event.unit as string;
    if (diffDays > oneYearDays) {
      if (incomingUnit !== 'quarter' && incomingUnit !== 'year') {
        incomingUnit = 'quarter';
      }
    } else if (diffDays > halfYearDays) {
      if (incomingUnit !== 'month' && incomingUnit !== 'quarter') {
        incomingUnit = 'month';
      }
    } else {
      if (incomingUnit !== 'month' && incomingUnit !== 'quarter' && incomingUnit !== 'week') {
        incomingUnit = 'week';
      }
    }

    const normalizeUnit = (u: string): ChartTimeUnit => {
      switch (u) {
        case 'week':
          return 'week';
        case 'month':
          return 'month';
        case 'quarter':
          return 'quarter';
        case 'year':
          return 'year';
        case 'day':
          return 'week';
        default:
          return 'week';
      }
    };
    this.chartUnit.set(normalizeUnit(incomingUnit));
    if (!(sameRange && hasData)) {
      if (this.canCallApi() && !this.isUnsupportedProduct()) { // We don't want to call the API before the productId is set, right now Date-picker component is not waiting for the productId to be set and emits the date event on load
        if (
          this.showSection(OverdueCronSectionId.ANALYTICS_SUMMARY) ||
          this.showSection(OverdueCronSectionId.VOLUME_TRENDS) ||
          this.showSection(OverdueCronSectionId.TRANSACTION_AMOUNTS) ||
          this.showSection(OverdueCronSectionId.STATUS_DISTRIBUTION) ||
          this.showSection(OverdueCronSectionId.METHOD_DISTRIBUTION) ||
          this.showSection(OverdueCronSectionId.REVENUE_DISTRIBUTION) ||
          this.showSection(OverdueCronSectionId.SUCCESS_RATE_TREND) ||
          this.showSection(OverdueCronSectionId.DETAILED_STATISTICS)
        ) {
          this.customRangeData({
            start_date: this.formatDateForApi(event.start_date),
            end_date: this.formatDateForApi(event.end_date),
          } as any);
        }
      }
    }
  }

  // Group payment trend data by selected time unit
  private getGroupKey(dateStr: string, unit: ChartTimeUnit): string {
    const d = dayjs.utc(dateStr);
    switch (unit) {
      case 'week':
        return d.format('YYYY-MM-DD');
      case 'month':
        return d.format('YYYY-MM');
      case 'quarter': {
        const quarterMonth = Math.floor(d.month() / 3) * 3 + 1; // 1, 4, 7, 10
        const formattedMonth = quarterMonth < 10 ? `0${quarterMonth}` : `${quarterMonth}`;
        return `${d.format('YYYY')}-${formattedMonth}-01`;
      }
      case 'year':
        return d.format('YYYY');
      default:
        return dateStr;
    }
  }

  private formatDateLabel(groupKey: string, unit: ChartTimeUnit): string {
    const d = dayjs.utc(groupKey);
    switch (unit) {
      case 'week':
        return d.format('MMM D, YYYY');
      case 'month':
        return d.format('MMM YYYY');
      case 'quarter':
        return `Q${Math.floor(d.month() / 3) + 1} ${d.format('YYYY')}`;
      case 'year':
        return d.format('YYYY');
      default:
        return groupKey;
    }
  }

  private formatTooltipDate(groupKey: string, unit: ChartTimeUnit): string {
    const d = dayjs.utc(groupKey);
    switch (unit) {
      case 'week':
        return d.format('MMMM D, YYYY');
      case 'month':
        return d.format('MMMM YYYY');
      case 'quarter':
        return `Q${Math.floor(d.month() / 3) + 1} ${d.format('YYYY')}`;
      case 'year':
        return d.format('YYYY');
      default:
        return groupKey;
    }
  }

  refreshCustomRangeData() {
    this.customRangeData({
      start_date: this.formatDateForApi(this.selectedStartDate()),
      end_date: this.formatDateForApi(this.selectedEndDate()),
    } as any);
  }

  alwaysAvailableUnits = computed<any>(() => {
    const start = dayjs(this.selectedStartDate());
    const end = dayjs(this.selectedEndDate());
    const diffDays = end.diff(start, 'day');
    const halfYearDays = 180;
    const oneYearDays = 365;
    
    if (diffDays > oneYearDays) {
      return ['quarter', 'year'];
    } else if (diffDays > halfYearDays) {
      return ['month', 'quarter'];
    }
    return 'week';
  });

  paymentStatsHeaders: TableHeader[] = [
    { label: 'Date', key: 'label', align: 'text-left', noTransform: true },
    { label: 'Total', key: 'totalPayments', align: 'text-right' },
    { label: 'Success', key: 'successPayments', align: 'text-right', textClass: 'text-green-600' },
    { label: 'Failed', key: 'failedPayments', align: 'text-right', textClass: 'text-red-600' },
    { label: 'Success Rate', key: 'successRate', align: 'text-right', noTransform: true },
    { label: 'Success Amount', key: 'successAmount', align: 'text-right', textClass: 'text-green-600' },
    { label: 'Failed Amount', key: 'failedAmount', align: 'text-right', textClass: 'text-red-600' },
  ];

  paymentStatsTableRows = computed(() => {
    const counts = this.groupedChartData();
    const amounts = this.groupedAmountData();
    if (!counts.length) return [];

    const amountMap = new Map<string, { successAmount: number; failedAmount: number; totalAmount: number }>();
    for (const a of amounts) {
      amountMap.set(a.date, {
        successAmount: a.successAmount,
        failedAmount: a.failedAmount,
        totalAmount: a.totalAmount,
      });
    }

    const rows = counts.map((c) => {
      const am = amountMap.get(c.date) || { successAmount: 0, failedAmount: 0, totalAmount: 0 };
      const successRate =
        c.totalPayments > 0 ? `${((c.successPayments / c.totalPayments) * 100).toFixed(1)}%` : '0%';
      return {
        date: c.date,
        label: c.label,
        totalPayments: c.totalPayments,
        successPayments: c.successPayments,
        failedPayments: c.failedPayments,
        successRate,
        successAmount: am.successAmount,
        failedAmount: am.failedAmount,
      };
    });

    // Sort rows from most recent to oldest
    return rows.sort((a, b) => {
      const dateA =
        a.date.length === 4
          ? `${a.date}-01-01`
          : a.date.length === 7
            ? `${a.date}-01`
            : a.date;
      const dateB =
        b.date.length === 4
          ? `${b.date}-01-01`
          : b.date.length === 7
            ? `${b.date}-01`
            : b.date;
      return new Date(dateB).getTime() - new Date(dateA).getTime();
    });
  });

  // Signals for payment trend data to trigger reactivity
  paymentVolumeTrend = signal<PaymentTrendItem[]>([]);
  paymentAmountTrend = signal<PaymentAmountItem[]>([]);
  successRateTrend = signal<{ label: string; value: number; successAmount: number }[]>([]);

  groupedChartData = computed(() => {
    const data = this.paymentVolumeTrend();
    const unit = this.chartUnit();

    if (!data || data.length === 0) return [];

    // Sort data by date first
    const sortedData = [...data].sort(
      (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
    );

    // Group by selected unit
    const groups = new Map<string, PaymentTrendItem[]>();

    for (const item of sortedData) {
      const key = this.getGroupKey(item.date, unit);
      if (!groups.has(key)) {
        groups.set(key, []);
      }
      groups.get(key)!.push(item);
    }

    // Aggregate each group
    const result: {
      date: string;
      label: string;
      tooltipLabel: string;
      successPayments: number;
      failedPayments: number;
      totalPayments: number;
    }[] = [];

    groups.forEach((items, key) => {
      const aggregated = items.reduce(
        (acc, item) => ({
          successPayments: acc.successPayments + item.successPayments,
          failedPayments: acc.failedPayments + item.failedPayments,
          totalPayments: acc.totalPayments + item.totalPayments,
        }),
        { successPayments: 0, failedPayments: 0, totalPayments: 0 }
      );

      result.push({
        date: key,
        label: this.formatDateLabel(key, unit),
        tooltipLabel: this.formatTooltipDate(key, unit),
        ...aggregated,
      });
    });

    // Sort by date - handle different date formats (YYYY-MM-DD, YYYY-MM, YYYY)
    return result.sort((a, b) => {
      const dateA =
        a.date.length === 4
          ? `${a.date}-01-01`
          : a.date.length === 7
            ? `${a.date}-01`
            : a.date;
      const dateB =
        b.date.length === 4
          ? `${b.date}-01-01`
          : b.date.length === 7
            ? `${b.date}-01`
            : b.date;
      return new Date(dateA).getTime() - new Date(dateB).getTime();
    });
  });

  // Line Area Chart Series Data from customRangeStats
  paymentVolumeSeries = computed<ILineAreaSeries[]>(() => {
    const data = this.groupedChartData();

    if (data.length === 0) {
      return [
        {
          key: 'failed',
          name: 'Failed',
          lineColor: '#ef4444',
          areaColor: '#ef4444',
          areaOpacity: 0.35,
          strokeWidth: 2,
          data: [],
        },
        {
          key: 'successful',
          name: 'Successful',
          lineColor: '#10b981',
          areaColor: '#10b981',
          areaOpacity: 0.35,
          strokeWidth: 2,
          data: [],
        },
      ];
    }

    return [
      {
        key: 'failed',
        name: 'Failed',
        lineColor: '#ef4444',
        areaColor: '#ef4444',
        areaOpacity: 0.35,
        strokeWidth: 2,
        data: data.map((d) => ({
          date: d.date,
          value: d.failedPayments,
          label: d.tooltipLabel,
        })),
      },
      {
        key: 'successful',
        name: 'Successful',
        lineColor: '#10b981',
        areaColor: '#10b981',
        areaOpacity: 0.35,
        strokeWidth: 2,
        data: data.map((d) => ({
          date: d.date,
          value: d.successPayments,
          label: d.tooltipLabel,
        })),
      },
    ];
  });

  // Grouped chart data for payment amounts
  groupedAmountData = computed(() => {
    const data = this.paymentAmountTrend();
    const unit = this.chartUnit();

    if (!data || data.length === 0) return [];

    // Sort data by date first
    const sortedData = [...data].sort(
      (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
    );

    // Group by selected unit
    const groups = new Map<string, PaymentAmountItem[]>();

    for (const item of sortedData) {
      const key = this.getGroupKey(item.date, unit);
      if (!groups.has(key)) {
        groups.set(key, []);
      }
      groups.get(key)!.push(item);
    }

    // Aggregate each group
    const result: {
      date: string;
      label: string;
      tooltipLabel: string;
      successAmount: number;
      failedAmount: number;
      totalAmount: number;
    }[] = [];

    groups.forEach((items, key) => {
      const aggregated = items.reduce(
        (acc, item) => ({
          successAmount: acc.successAmount + item.successAmount,
          failedAmount: acc.failedAmount + item.failedAmount,
          totalAmount: acc.totalAmount + item.totalAmount,
        }),
        { successAmount: 0, failedAmount: 0, totalAmount: 0 }
      );

      result.push({
        date: key,
        label: this.formatDateLabel(key, unit),
        tooltipLabel: this.formatTooltipDate(key, unit),
        ...aggregated,
      });
    });

    // Sort by date - handle different date formats (YYYY-MM-DD, YYYY-MM, YYYY)
    return result.sort((a, b) => {
      const dateA =
        a.date.length === 4
          ? `${a.date}-01-01`
          : a.date.length === 7
            ? `${a.date}-01`
            : a.date;
      const dateB =
        b.date.length === 4
          ? `${b.date}-01-01`
          : b.date.length === 7
            ? `${b.date}-01`
            : b.date;
      return new Date(dateA).getTime() - new Date(dateB).getTime();
    });
  });

  // Line Area Chart Series Data for payment amounts
  paymentAmountSeries = computed<ILineAreaSeries[]>(() => {
    const data = this.groupedAmountData();

    if (data.length === 0) {
      return [
        {
          key: 'failed',
          name: 'Failed Amount',
          lineColor: '#ef4444',
          areaColor: '#ef4444',
          areaOpacity: 0.35,
          strokeWidth: 2,
          data: [],
        },
        {
          key: 'successful',
          name: 'Success Amount',
          lineColor: '#3b82f6',
          areaColor: '#3b82f6',
          areaOpacity: 0.35,
          strokeWidth: 2,
          data: [],
        },
      ];
    }

    return [
      {
        key: 'failed',
        name: 'Failed Amount',
        lineColor: '#ef4444',
        areaColor: '#ef4444',
        areaOpacity: 0.35,
        strokeWidth: 2,
        data: data.map((d) => ({
          date: d.date,
          value: d.failedAmount,
          label: d.tooltipLabel,
        })),
      },
      {
        key: 'successful',
        name: 'Success Amount',
        lineColor: '#3b82f6',
        areaColor: '#3b82f6',
        areaOpacity: 0.35,
        strokeWidth: 2,
        data: data.map((d) => ({
          date: d.date,
          value: d.successAmount,
          label: d.tooltipLabel,
        })),
      },
    ];
  });

  customRangeStats = {
    loading: false,
    startDate: '',
    endDate: '',
    dataPoints: 0,
    totalAmount: 0,
    data: {
      stat1: {
        total_payments: 0,
        successPayments: 0,
        successRate: 0,
        failedPayments: 0,
        failedRate: 0,
        totalRevenue: 0,
        cardPayments: 0,
        achPayments: 0,
        pendingPayments: 0,
      },
      payment_volume_trend: [],
      payment_amount_trend: [],
      revenue_distribution: [
        {
          label: 'Successful Revenue',
          value: 0,
        },
        {
          label: 'Failed Attempts',
          value: 0,
        },
        {
          label: 'Pending Revenue',
          value: 0,
        },
      ],
      successRateDistribution: []
    },
  };

  // Signals for pie chart data to ensure reactivity
  private pieChartDataSignal = signal({
    cardPayments: 0,
    achPayments: 0,
    successPayments: 0,
    failedPayments: 0,
    pendingPayments: 0,
  });

  // Pie Chart Data - Payment Method Distribution
  paymentMethodDistribution = computed(() => {
    const data = this.pieChartDataSignal();

    if (!data.cardPayments && !data.achPayments) {
      return [];
    }

    return [
      {
        label: 'Card Payments',
        value: data.cardPayments,
      },
      {
        label: 'ACH Payments',
        value: data.achPayments,
      },
    ].filter((item) => item.value > 0); // Only show non-zero values
  });

  // Pie Chart Data - Payment Status Distribution
  paymentStatusDistribution = computed(() => {
    const data = this.pieChartDataSignal();

    if (
      !data.successPayments &&
      !data.failedPayments &&
      !data.pendingPayments
    ) {
      return [];
    }

    return [
      {
        label: 'Success',
        value: data.successPayments,
      },
      {
        label: 'Failed',
        value: data.failedPayments,
      },
      {
        label: 'Pending',
        value: data.pendingPayments,
      },
    ].filter((item) => item.value > 0); // Only show non-zero values
  });

  // Grouped success rate data (same pattern as other charts)
  groupedSuccessRateData = computed(() => {
    const data = this.successRateTrend();
    const unit = this.chartUnit();

    if (!data || data.length === 0) return [];

    // Sort data by date first
    const sortedData = [...data].sort(
      (a, b) => new Date(a.label).getTime() - new Date(b.label).getTime()
    );

    // Group by selected unit
    const groups = new Map<string, { label: string; value: number; successAmount: number }[]>();

    for (const item of sortedData) {
      const key = this.getGroupKey(item.label, unit);
      if (!groups.has(key)) {
        groups.set(key, []);
      }
      groups.get(key)!.push(item);
    }

    // Aggregate each group (average success rate, sum success amount)
    const result: {
      date: string;
      label: string;
      tooltipLabel: string;
      successRate: number;
      successAmount: number;
    }[] = [];

    groups.forEach((items, key) => {
      const avgRate =
        items.reduce((acc, item) => acc + item.value, 0) / items.length;
      const totalAmount =
        items.reduce((acc, item) => acc + item.successAmount, 0);

      result.push({
        date: key,
        label: this.formatDateLabel(key, unit),
        tooltipLabel: this.formatTooltipDate(key, unit),
        successRate: avgRate,
        successAmount: totalAmount,
      });
    });

    // Sort by date
    return result.sort((a, b) => {
      const dateA =
        a.date.length === 4
          ? `${a.date}-01-01`
          : a.date.length === 7
            ? `${a.date}-01`
            : a.date;
      const dateB =
        b.date.length === 4
          ? `${b.date}-01-01`
          : b.date.length === 7
            ? `${b.date}-01`
            : b.date;
      return new Date(dateA).getTime() - new Date(dateB).getTime();
    });
  });

  // Success Rate Series for line-area chart
  successRateSeries = computed<ILineAreaSeries[]>(() => {
    const data = this.groupedSuccessRateData();

    if (data.length === 0) {
      return [
        {
          key: 'successRate',
          name: 'Success Rate (%)',
          lineColor: '#8B5CF6',
          areaColor: '#8B5CF6',
          areaOpacity: 0,
          strokeWidth: 2,
          showPoints: true,
          pointRadius: 4,
          data: [],
        },
      ];
    }

    return [
      {
        key: 'successRate',
        name: 'Success Rate (%)',
        lineColor: '#8B5CF6',
        areaColor: '#8B5CF6',
        areaOpacity: 0,
        strokeWidth: 2,
        showPoints: true,
        pointRadius: 4,
        data: data.map((d) => ({
          date: d.date,
          value: d.successRate,
          label: d.tooltipLabel,
          metadata: {
            successAmount: d.successAmount,
          },
        })),
      },
    ];
  });

  successAmountSeries = computed<ILineAreaSeries[]>(() => {
    const data = this.groupedSuccessRateData();

    if (data.length === 0) {
      return [
        {
          key: 'successAmount',
          name: 'Success Amount',
          lineColor: '#8B5CF6',
          areaColor: '#8B5CF6',
          areaOpacity: 0,
          strokeWidth: 2,
          showPoints: !this.isDarkTheme(),
          pointRadius: 4,
          data: [],
        },
      ];
    }

    return [
      {
        key: 'successAmount',
        name: 'Success Amount',
        lineColor: '#8B5CF6',
        areaColor: '#8B5CF6',
        areaOpacity: 0,
        strokeWidth: 2,
        showPoints: true,
        pointRadius: 4,
        data: data.map((d) => ({
          date: d.date,
          value: d.successAmount,
          label: d.tooltipLabel,
        })),
      },
    ];
  });

  chartMetric = signal<'rate' | 'amount'>('rate');

  metricToggleHeaderOptions: any = {
    heading: '',
    showTable: false,
    showlabels: true,
    showToggleButton: true,
    headingColor: '#131921',
    headingCursor: '',
    headerChangeView: false,
    showHeading: false,
    iconPath: '',
    showIcon: false,
    fontSize: '14px',
    toggleOptions: [
      { label: 'Success Rate', tooltip: 'View Success Rate', isTable: false, value: 'rate' },
      { label: 'Success Amount', tooltip: 'View Success Amount', isTable: true, value: 'amount' },
    ],
  };

  setMetricSelection(value: any) {
    const resolved = value === 'table' ? 'amount' : value === 'graph' ? 'rate' : value;
    this.chartMetric.set(resolved === 'amount' ? 'amount' : 'rate');
  }

  currentSuccessSeries = computed<ILineAreaSeries[]>(() => {
    return this.chartMetric() === 'rate' ? this.successRateSeries() : this.successAmountSeries();
  });

  currentSuccessTitle = computed(() => {
    return this.chartMetric() === 'rate' ? 'Success Rate Trend' : 'Success Amount Trend';
  });

  currentSuccessTooltip = computed(() => {
    return this.chartMetric() === 'rate'
      ? { show: true, showHoverLine: true, showTotal: false, valueFormat: 'percent' as const }
      : { show: true, showHoverLine: true, showTotal: false, valueFormat: 'currency' as const };
  });

  customRangeData(payload?: { start_date: string; end_date: string }) {
    this.customRangeStats.loading = true;
    this.cdr.markForCheck();
    const url = this.productConfig().overdue_cron_sub_url || '/get_friday_cron_data';

    this.apiService
      .post(
        url,
        payload ?? {
          start_date: this.formatDateForApi(this.selectedStartDate()),
          end_date: this.formatDateForApi(this.selectedEndDate()),
        },
        {
          addAccessToken: false,
          addSecurityHeaders: false,
          productServerName: this.productServerName(),
          showLoader: false,
        }
      )
      .subscribe({
        next: (response: any) => {
          this.customRangeStats.loading = false;

          this.customRangeStats.startDate = this.selectedStartDate();
          this.customRangeStats.endDate = this.selectedEndDate();
          this.customRangeStats.dataPoints = response.count;

          const data = response.data;

          const totalPayments = data.reduce(
            (acc: number, item: any) => acc + item.total_payments,
            0
          );
          const successPayments = data.reduce(
            (acc: number, item: any) =>
              acc + item.success_card_payments + item.success_ach_payments,
            0
          );
          const failedPayments = data.reduce(
            (acc: number, item: any) =>
              acc + item.failed_card_payments + item.failed_ach_payments,
            0
          );
          const cardPayments = data.reduce(
            (acc: number, item: any) => acc + item.card_payments,
            0
          );
          const achPayments = data.reduce(
            (acc: number, item: any) => acc + item.ach_payments,
            0
          );
          const pendingPayments = data.reduce(
            (acc: number, item: any) => acc + item.pending_ach_payments,
            0
          );
          const totalAmount = data.reduce(
            (acc: number, item: any) =>
              acc +
              item.success_card_payments_amount +
              item.success_ach_payments_amount +
              item.failed_card_payments_amount +
              item.failed_ach_payments_amount +
              item.pending_ach_payments_amount,
            0
          );

          this.customRangeStats.totalAmount = totalAmount;

          this.customRangeStats.data = {
            stat1: {
              total_payments: totalPayments,
              successPayments: successPayments,
              successRate:
                totalPayments > 0
                  ? Number(((successPayments / totalPayments) * 100).toFixed(2))
                  : 0,
              failedPayments: failedPayments,
              failedRate:
                totalPayments > 0
                  ? Number(((failedPayments / totalPayments) * 100).toFixed(2))
                  : 0,
              totalRevenue: data.reduce(
                (acc: number, item: any) =>
                  acc +
                  item.success_card_payments_amount +
                  item.success_ach_payments_amount,
                0
              ),
              cardPayments: cardPayments,
              achPayments: achPayments,
              pendingPayments: pendingPayments,
            },

            payment_volume_trend: data.map((item: any) => {
              return {
                date: item.DATE,
                successPayments:
                  item.success_card_payments + item.success_ach_payments,
                failedPayments:
                  item.failed_card_payments + item.failed_ach_payments,
                totalPayments: item.total_payments,
              };
            }),

            payment_amount_trend: data.map((item: any) => {
              return {
                date: item.DATE,
                successAmount:
                  item.success_card_payments_amount +
                  item.success_ach_payments_amount,
                failedAmount:
                  item.failed_card_payments_amount +
                  item.failed_ach_payments_amount,
                totalAmount: item.total_payments_amount,
              };
            }),

            revenue_distribution: [
              {
                label: 'Successful Revenue',
                value: data.reduce(
                  (acc: number, item: any) =>
                    acc +
                    item.success_card_payments_amount +
                    item.success_ach_payments_amount,
                  0
                ),
              },
              {
                label: 'Failed Attempts',
                value: data.reduce(
                  (acc: number, item: any) =>
                    acc +
                    item.failed_card_payments_amount +
                    item.failed_ach_payments_amount,
                  0
                ),
              },
              {
                label: 'Pending Revenue',
                value: data.reduce(
                  (acc: number, item: any) =>
                    acc + item.pending_ach_payments_amount,
                  0
                ),
              },
            ],

            successRateDistribution: data.map((item: any) => {
              const totalPaymentsCount = item.total_payments;
              const successPaymentsCount = item.success_card_payments + item.success_ach_payments;
              const successAmount = item.success_card_payments_amount + item.success_ach_payments_amount;

              const successRate = totalPaymentsCount > 0 ? Number(((successPaymentsCount / totalPaymentsCount) * 100).toFixed(2)) : 0;

              return {
                label: item.DATE,
                value: successRate,
                successAmount: successAmount,
              };
            })
          };

          // Update the signals to trigger chart reactivity
          this.paymentVolumeTrend.set(
            this.customRangeStats.data.payment_volume_trend
          );
          this.paymentAmountTrend.set(
            this.customRangeStats.data.payment_amount_trend
          );
          this.successRateTrend.set(
            this.customRangeStats.data.successRateDistribution
          );

          // Update pie chart data signal
          this.pieChartDataSignal.set({
            cardPayments: cardPayments,
            achPayments: achPayments,
            successPayments: successPayments,
            failedPayments: failedPayments,
            pendingPayments: pendingPayments,
          });

          // Notify Angular's change detector that customRangeStats (a plain
          // mutable object, not a signal) has been updated. Without this,
          // the ANALYTICS_SUMMARY card (which reads customRangeStats.data.stat1)
          // will not re-render when embedded inside an OnPush widget-host on
          // My Dashboard — the stat values would stay stale after filter changes.
          this.cdr.markForCheck();

          // Manually trigger pie chart rendering after data loads
          setTimeout(() => {
            this.renderPaymentMethodPieChart();
            this.renderPaymentStatusPieChart();
          }, 100);
        },
        error: (error: any) => {
          console.error('customRangeData error:', JSON.stringify(error, null, 2));
          this.customRangeStats.loading = false;
          this.cdr.markForCheck();
        },
      });
  }

  // Pie Chart Rendering Methods
  private getChartColors() {
    const isDark = this.currentTheme === 'dark';
    return {
      background: isDark ? '#232327' : '#F5F5F5',
      text: isDark ? '#ffffff' : '#141437',
      shadow: isDark
        ? '0px 4px 20px rgba(0,0,0,0.35)'
        : '0px 4px 20px rgba(0,0,0,0.15)',
      border: isDark ? '1px solid #2A2A2E' : '1px solid #EAEAEA',
      subtitleColor: isDark ? '#A1A1AA' : '#7D7D93',
      // Colors matching the screenshot
      paymentMethodColors: ['#4F7CFF', '#10B981'], // Blue for Card, Green for ACH
      paymentStatusColors: ['#10B981', '#EF4444', '#F59E0B'], // Green for Success, Red for Failed, Orange for Pending
    };
  }

  private renderPieChart(
    containerId: string,
    legendId: string,
    data: PieChartData[],
    colorPalette: string[]
  ): void {
    if (!data || data.length === 0) {
      return;
    }

    const containerElement = document.getElementById(containerId);
    if (!containerElement) {
      return;
    }

    const container = d3.select(`#${containerId}`);
    const legendContainer = d3.select(`#${legendId}`);

    container.selectAll('*').remove();
    legendContainer.selectAll('*').remove();

    const containerWidth = containerElement.clientWidth;
    const containerHeight = containerElement.clientHeight;

    if (containerWidth === 0 || containerHeight === 0) {
      return;
    }

    // Calculate dimensions for donut chart
    const size = 200;
    const radius = size / 2 - 10;
    const innerRadius = 0;

    const colors = this.getChartColors();
    const colorScale = d3.scaleOrdinal(colorPalette);

    const total = d3.sum(data, (d) => d.value);

    // Create SVG for donut chart
    const svg = container
      .append('svg')
      .attr('width', size)
      .attr('height', size)
      .append('g')
      .attr('transform', `translate(${size / 2}, ${size / 2})`);

    const pie = d3
      .pie<PieChartData>()
      .value((d) => d.value)
      .sort(null)
      .padAngle(0.02) // Add padding between slices
      .startAngle(-Math.PI / 2 + Math.PI) // rotate by 180°
      .endAngle(Math.PI * 1.5 + Math.PI); // rotate by 180°

    const arc = d3
      .arc<any>()
      .innerRadius(innerRadius)
      .outerRadius(radius)
      .cornerRadius(2); // Slight rounding on corners

    const hoverArc = d3
      .arc<any>()
      .innerRadius(innerRadius)
      .outerRadius(radius + 5)
      .cornerRadius(2);

    // Tooltip
    const tooltip = container
      .append('div')
      .attr('class', 'pie-tooltip')
      .style('opacity', 0)
      .style('position', 'absolute')
      .style('pointer-events', 'none')
      .style('background', colors.background)
      .style('border', colors.border)
      .style('border-radius', '8px')
      .style('padding', '12px 16px')
      .style('box-shadow', colors.shadow)
      .style('color', colors.text)
      .style('font-family', 'Inter, sans-serif')
      .style('font-size', '13px')
      .style('white-space', 'nowrap')
      .style('z-index', '1000');

    const arcs = svg
      .selectAll('g.slice')
      .data(pie(data))
      .enter()
      .append('g')
      .attr('class', 'slice');

    // Draw pie slices
    arcs
      .append('path')
      .attr('d', arc)
      .attr('fill', (_, i) => colorScale(i.toString()))
      .style('transition', 'all 0.2s ease-out')
      .style('cursor', 'pointer')
      .on('mouseover', function (event, d) {
        d3.select(this)
          .transition()
          .duration(200)
          .attr('d', hoverArc(d))
          .style('filter', 'drop-shadow(0px 2px 4px rgba(0,0,0,0.2))');

        const pct = ((d.data.value / total) * 100).toFixed(1);
        tooltip.style('opacity', 1).html(`
            <div style="font-weight:600; color:${colors.subtitleColor
          }; margin-bottom:4px;">
              ${d.data.label}
            </div>
            <div style="font-weight:500; color:${colors.text};">
              ${d.data.value.toLocaleString()} (${pct}%)
            </div>
          `);
      })
      .on('mousemove', (event) => {
        const containerRect = containerElement.getBoundingClientRect();
        tooltip
          .style('left', `${event.clientX - containerRect.left + 15}px`)
          .style('top', `${event.clientY - containerRect.top - 15}px`);
      })
      .on('mouseout', function (event, d) {
        d3.select(this)
          .transition()
          .duration(200)
          .attr('d', arc)
          .style('filter', 'none');

        tooltip.style('opacity', 0);
      });

    // Create legend on the right side with stats
    legendContainer
      .selectAll('div')
      .data(data)
      .enter()
      .append('div')
      .attr('class', 'flex items-center justify-between gap-3 py-1 w-full')
      .html((d, i) => {
        const pct = ((d.value / total) * 100).toFixed(1);

        return `
      <div class="flex items-center gap-3">
        <span style="
          width: 10px;
          height: 10px;
          background: ${colorScale(i.toString())};
          border-radius: 50%;
          flex-shrink: 0;">
        </span>

        <span style="color: ${colors.text}; font-size: 14px;">
          ${d.label}
        </span>
      </div>

      <div class="flex items-center gap-2 justify-end">
        <span style="color: ${colors.text}; font-size: 15px;">
          ${d.value.toLocaleString()}
        </span>

        <span style="color: ${colors.subtitleColor}; font-size: 13px;">
          (${pct}%)
        </span>
      </div>
    `;
      });
  }

  private renderPaymentMethodPieChart(): void {
    const data = this.paymentMethodDistribution();
    const colors = this.getChartColors();
    this.renderPieChart(
      'paymentMethodPieChart',
      'paymentMethodLegend',
      data,
      colors.paymentMethodColors
    );
  }

  private renderPaymentStatusPieChart(): void {
    const data = this.paymentStatusDistribution();
    const colors = this.getChartColors();
    this.renderPieChart(
      'paymentStatusPieChart',
      'paymentStatusLegend',
      data,
      colors.paymentStatusColors
    );
  }

  // Get color gradient for revenue bars
  getRevenueBarColor(label: string): string {
    switch (label) {
      case 'Successful Revenue':
        return 'linear-gradient(to right,rgb(20, 190, 171), #14b8a6)'; // Teal gradient
      case 'Failed Attempts':
        return 'linear-gradient(to right, #f87171, #ef4444)'; // Red gradient
      case 'Pending Revenue':
        return 'linear-gradient(to right, #cbd5e1, #94a3b8)'; // Gray gradient
      default:
        return 'linear-gradient(to right, #3b82f6, #2563eb)'; // Blue gradient (fallback)
    }
  }

  // Custom tooltip formatter for success rate chart
  formatSuccessRateTooltip = (value: number, series: ILineAreaSeries, point: any): string => {
    const successAmount = point.metadata?.successAmount || 0;
    return `${value.toFixed(1)}% (${successAmount.toLocaleString('en-US', { style: 'currency', currency: 'USD' })})`;
  }

  // ─── Customize Layout (visibility + colSpan + order) ───────────────────
  /** Persisted layout map driving the actual rendered page. */
  readonly layoutMap = signal<Record<OverdueCronSectionId, OverdueCronSectionLayoutItem>>(
    {} as Record<OverdueCronSectionId, OverdueCronSectionLayoutItem>,
  );

  /** All sections sorted by their persisted order. */
  readonly orderedSections = computed(() =>
    Object.values(this.layoutMap()).slice().sort((a, b) => a.order - b.order),
  );

  /** Sections currently visible, in order — used by the live grid. */
  readonly orderedVisibleSections = computed(() =>
    this.orderedSections().filter((s) => s.visible),
  );

  /** Standalone page: all visible sections; embedded: allow-list from parent. */
  readonly embeddedVisibleSections = createEmbeddedSectionFilter(
    this.orderedVisibleSections,
    this.mixedMode,
    this.visibleWidgetKeys,
    this.orderedSections,
  );

  private readonly activeSectionIds = computed<ReadonlySet<string>>(() => {
    const keys = this.visibleWidgetKeys();
    if (!keys || keys.length === 0) return new Set();
    const allowed = new Set<string>();
    for (const k of keys) {
      if (!k) continue;
      allowed.add(k);
      const canonical = stripProductPrefix(k);
      if (canonical !== k) allowed.add(canonical);
    }
    return allowed;
  });

  showSection(id: OverdueCronSectionId): boolean {
    if (this.mixedMode()) {
      const active = this.activeSectionIds();
      return active.has(id);
    }
    return this.layoutMap()[id]?.visible ?? true;
  }

  /** Visible sections that belong to the top "overview" grid (most-recent
   *  payment attempt). Rendered above the static date picker. */
  readonly overviewSections = computed(() =>
    this.embeddedVisibleSections().filter((s) =>
      this.OVERVIEW_SECTION_IDS.has(s.id),
    ),
  );

  /** Visible sections that belong to the bottom "analytics" grid
   *  (date-range driven charts / table). */
  readonly analyticsSections = computed(() =>
    this.embeddedVisibleSections().filter(
      (s) => !this.OVERVIEW_SECTION_IDS.has(s.id),
    ),
  );

  /** Tailwind col-span class for a section in the live 12-col grid. */
  getSectionColClass(item: OverdueCronSectionLayoutItem | OverdueCronSectionId): string {
    const id = typeof item === 'string' ? item : item.id;
    const span = this._normalizeColSpan(this.layoutMap()[id]?.colSpan ?? 12);
    switch (span) {
      case 4: return 'col-span-12 lg:col-span-4';
      case 6: return 'col-span-12 lg:col-span-6';
      case 8: return 'col-span-12 lg:col-span-8';
      case 12: default: return 'col-span-12';
    }
  }

  trackSection = (_idx: number, sec: OverdueCronSectionLayoutItem): string => sec.id;

  // ─── "Pin to Custom Dashboard" (API-backed, via CustomDashboardPinService) ───
  readonly pinnedSections = signal<Set<OverdueCronSectionId>>(new Set());
  readonly pendingPinSections = signal<Set<OverdueCronSectionId>>(new Set());

  /**
   * Catalog `widgets.id` resolved per section, populated by the
   * constructor effect once the catalog products + page id resolve.
   * Empty when backend hasn't registered Overdue Cron Payments yet —
   * which is also the signal {@link canPinToCustomDashboard} uses to
   * gate the pin button so it never appears for sections that can't
   * actually be pinned.
   */
  readonly pinnableWidgetIds = signal<Map<OverdueCronSectionId, number>>(
    new Map(),
  );

  private _toSectionId(
    sec: { id: OverdueCronSectionId } | OverdueCronSectionId,
  ): OverdueCronSectionId {
    return typeof sec === 'object' && sec !== null ? sec.id : sec;
  }

  isAddedToCustomDashboard(
    sec: { id: OverdueCronSectionId } | OverdueCronSectionId,
  ): boolean {
    return this.pinnedSections().has(this._toSectionId(sec));
  }

  isAddingToCustomDashboard(
    sec: { id: OverdueCronSectionId } | OverdueCronSectionId,
  ): boolean {
    return this.pendingPinSections().has(this._toSectionId(sec));
  }

  /**
   * Returns `true` when the page renders standalone — resolution against
   * the dashboards backend catalog happens lazily at click time.
   */
  canPinToCustomDashboard(
    sec: { id: OverdueCronSectionId } | OverdueCronSectionId,
  ): boolean {
    if (this.mixedMode()) return false;
    if (!this._toSectionId(sec)) return false;
    return true;
  }

  /**
   * Quick-action toggle handler. Mirrors daily-cron / campaign-ads-stats:
   *  • Fast path uses the pre-resolved widget id when available.
   *  • Fallback uses {@link CustomDashboardPinService.toggleSection}
   *    which performs the catalog lookup on click.
   */
  addToCustomDashboard(
    sec: { id: OverdueCronSectionId } | OverdueCronSectionId,
  ): void {
    const sectionId = this._toSectionId(sec);
    if (!sectionId) return;
    if (this.pendingPinSections().has(sectionId)) return;

    const pageId = this.effectiveCatalogPageId();
    if (pageId == null) {
      this.toaster?.info?.(
        'Pinning Overdue Cron sections to your Custom Dashboard will be available once this page is registered in the dashboards catalog.',
      );
      return;
    }

    const colSpan = this.layoutMap()[sectionId]?.colSpan ?? 12;
    const isCurrentlyPinned = this.isAddedToCustomDashboard(sectionId);

    const onPinnedChange = (pinned: boolean) => {
      this.pinnedSections.update((s) => {
        const next = new Set(s);
        if (pinned) next.add(sectionId);
        else next.delete(sectionId);
        return next;
      });
    };

    const onPendingChange = (pending: boolean) => {
      this.pendingPinSections.update((s) => {
        const next = new Set(s);
        if (pending) next.add(sectionId);
        else next.delete(sectionId);
        return next;
      });
    };

    const widgetId = this.pinnableWidgetIds().get(sectionId);
    if (widgetId != null) {
      this.customDashboardPin.toggleLayoutItem({
        pageId,
        widgetId,
        sourcePageId: pageId,
        colSpan,
        rowSpan: 1,
        customConfig: {},
        productId: resolveActiveCatalogProductId(this.apiService),
        isCurrentlyPinned,
        onPinnedChange,
        onPendingChange,
      });
      return;
    }

    this.customDashboardPin.toggleSection({
      pageId,
      widgetKey: sectionId,
      colSpan,
      isCurrentlyPinned,
      onPinnedChange,
      onPendingChange,
    });
  }

  // ─── Customize Layout editor (mini-dashboard preview) ──────────────────
  readonly isLayoutEditorOpen = signal(false);
  readonly editorItems = signal<OverdueCronLayoutEditorItem[]>([]);

  readonly editorVisibleCount = computed(
    () => this.editorItems().filter((i) => i.visible).length,
  );

  trackEditorItem = (_idx: number, item: OverdueCronLayoutEditorItem): string => item.id;

  /** Allowed col-span steps the user can cycle through per card. */
  private readonly COL_SPAN_STEPS: ReadonlyArray<number> = [4, 6, 8, 12];

  /** localStorage key for this page's persisted layout. */
  private readonly LAYOUT_LS_KEY = 'prism_overdue_cron_layout_v2';

  /** Default col-span per section — chosen so the live page renders with
   *  the same proportions as today (full-width "most recent" card,
   *  full-width metric strip, two charts side-by-side at 6/6, three
   *  pies/distribution cards at 4/4/4, full-width success trend, and
   *  full-width detailed stats table). */
  private readonly DEFAULT_COL_SPAN: Record<OverdueCronSectionId, number> = {
    [OverdueCronSectionId.RECENT_ATTEMPT_OVERVIEW]: 12,
    [OverdueCronSectionId.ANALYTICS_SUMMARY]: 12,
    [OverdueCronSectionId.VOLUME_TRENDS]: 6,
    [OverdueCronSectionId.TRANSACTION_AMOUNTS]: 6,
    [OverdueCronSectionId.STATUS_DISTRIBUTION]: 4,
    [OverdueCronSectionId.METHOD_DISTRIBUTION]: 4,
    [OverdueCronSectionId.REVENUE_DISTRIBUTION]: 4,
    [OverdueCronSectionId.SUCCESS_RATE_TREND]: 12,
    [OverdueCronSectionId.DETAILED_STATISTICS]: 12,
  };

  openCustomizeLayout(): void {
    const titleById = new Map(this.SECTIONS.map((s) => [s.id, s.title]));
    const items: OverdueCronLayoutEditorItem[] = this.orderedSections().map((s) => ({
      id: s.id,
      title: titleById.get(s.id) ?? s.id,
      visible: s.visible,
      colSpan: this._normalizeColSpan(s.colSpan),
      order: s.order,
      group: this.getSectionGroup(s.id),
    }));
    this.editorItems.set(items);
    this.isLayoutEditorOpen.set(true);
  }

  closeLayoutEditor(): void {
    this.isLayoutEditorOpen.set(false);
  }

  toggleEditorCardVisibility(id: OverdueCronSectionId): void {
    this.editorItems.update((items) =>
      this._reorderItemsOnVisibilityToggle(items, id),
    );
  }

  resetLayoutEditorToDefaults(): void {
    const titleById = new Map(this.SECTIONS.map((s) => [s.id, s.title]));
    const defaults: OverdueCronLayoutEditorItem[] = this.SECTIONS.map((s, idx) => ({
      id: s.id as OverdueCronSectionId,
      title: titleById.get(s.id) ?? s.id,
      visible: true,
      colSpan: this._normalizeColSpan(
        this.DEFAULT_COL_SPAN[s.id as OverdueCronSectionId] ?? 12,
      ),
      order: idx,
      group: this.getSectionGroup(s.id as OverdueCronSectionId),
    }));
    this.editorItems.set(defaults);
  }

  applyLayoutEditor(): void {
    const draft = this.editorItems();
    const layout: Record<OverdueCronSectionId, OverdueCronSectionLayoutItem> =
      {} as Record<OverdueCronSectionId, OverdueCronSectionLayoutItem>;
    draft.forEach((d, idx) => {
      layout[d.id] = {
        id: d.id,
        visible: d.visible,
        colSpan: this._normalizeColSpan(d.colSpan),
        order: idx,
      };
    });
    this.layoutMap.set(layout);
    this._saveLayoutToStorage(layout);

    // Best-effort backend persistence — local layout above is unchanged
    // regardless of network outcome.
    this._persistLayoutToBackend(draft);

    this.isLayoutEditorOpen.set(false);

    // Re-render imperative pie charts after DOM nodes may have moved or reappeared.
    requestAnimationFrame(() => {
      this.renderPaymentMethodPieChart();
      this.renderPaymentStatusPieChart();
    });
  }

  /**
   * Writes the editor draft to the user's per-page personalized
   * dashboard. Mirrors daily-cron / campaign-ads-stats — same fast/slow
   * path branching so the call always fires regardless of whether the
   * reactive catalog effects in the constructor have hydrated yet.
   * No-op when the catalog hasn't registered the page (the local
   * layoutMap + localStorage still update so the user's UI changes
   * are not lost).
   */
  private _persistLayoutToBackend(
    draft: ReadonlyArray<OverdueCronLayoutEditorItem>,
  ): void {
    const cachedPageId = this.effectiveCatalogPageId();
    const cachedWidgetMap = this.pinnableWidgetIds();
    const productId = resolveActiveCatalogProductId(this.apiService);

    if (cachedPageId != null && cachedWidgetMap.size > 0) {
      const batchDraft = draft
        .filter((d) => cachedWidgetMap.has(d.id))
        .map((d) => ({
          widgetId: cachedWidgetMap.get(d.id) ?? 0,
          sourcePageId: cachedPageId,
          colSpan: this._normalizeColSpan(d.colSpan),
          visible: d.visible,
          customConfig: null,
          order: d.order,
        }));

      if (batchDraft.length) {
        this._customizeLayoutBatch.applyEditorDraftToPageDashboard({
          pageId: cachedPageId,
          productId,
          fallbackSourcePageId: cachedPageId,
          draft: batchDraft,
        });
        return;
      }
    }

    if (cachedPageId == null) {
      // Catalog never resolved (page not registered yet) — skip the
      // backend call entirely. Local layout state is already persisted.
      return;
    }

    this.dashboardLayoutService
      .getCatalogProducts()
      .pipe(catchError(() => of([] as any[])))
      .subscribe(() => {
        this._customizeLayoutBatch.applyEditorDraftToPageDashboardByKeys({
          pageId: cachedPageId,
          productId,
          fallbackSourcePageId: cachedPageId,
          draft: draft.map((d, idx) => ({
            key: String(d.id),
            sourcePageId: cachedPageId,
            colSpan: this._normalizeColSpan(d.colSpan),
            visible: d.visible,
            customConfig: null,
            order: idx,
          })),
        });
      });
  }

  // ─── Editor: width chips (decrease / increase / labels) ─────────────────
  /** Steps a card down one width. Respects each card's designed default as the lower bound. */
  decreaseEditorCardColSpan(id: OverdueCronSectionId): void {
    this.editorItems.update((items) =>
      items.map((i) => {
        if (i.id !== id) return i;
        const idx = this.COL_SPAN_STEPS.indexOf(this._normalizeColSpan(i.colSpan));
        if (idx <= 0) return i;
        const nextSpan = this.COL_SPAN_STEPS[idx - 1];
        if (this._isBelowDefaultColSpan(i.id, nextSpan)) return i;
        return { ...i, colSpan: nextSpan };
      }),
    );
  }

  increaseEditorCardColSpan(id: OverdueCronSectionId): void {
    this.editorItems.update((items) =>
      items.map((i) => {
        if (i.id !== id) return i;
        const idx = this.COL_SPAN_STEPS.indexOf(this._normalizeColSpan(i.colSpan));
        if (idx < 0 || idx >= this.COL_SPAN_STEPS.length - 1) return i;
        return { ...i, colSpan: this.COL_SPAN_STEPS[idx + 1] };
      }),
    );
  }

  autoAdjustEditorWidths(): void {
    const updates = computeLayoutEditorAutoWidthUpdates(this.editorItems());
    if (!updates.size) return;
    this.editorItems.update((items) =>
      applyLayoutEditorAutoWidthUpdates(items, updates),
    );
  }

  canAutoAdjustEditorWidths(): boolean {
    return computeLayoutEditorAutoWidthUpdates(this.editorItems()).size > 0;
  }

  /**
   * Whether the minimize button should be active for this card. A card
   * cannot shrink below its designed default col-span — e.g. a card
   * designed for `col-8` can only step between 8 and 12.
   */
  canDecreaseEditorCardColSpan(item: OverdueCronLayoutEditorItem): boolean {
    const idx = this.COL_SPAN_STEPS.indexOf(this._normalizeColSpan(item.colSpan));
    if (idx <= 0) return false;
    if (this._isBelowDefaultColSpan(item.id, this.COL_SPAN_STEPS[idx - 1])) {
      return false;
    }
    return true;
  }

  /** Whether `span` is below the designed default col-span for `id`. */
  private _isBelowDefaultColSpan(
    id: OverdueCronSectionId,
    span: number,
  ): boolean {
    const defaultSpan = this.DEFAULT_COL_SPAN[id];
    if (defaultSpan == null) return false;
    return span < this._normalizeColSpan(defaultSpan);
  }

  canIncreaseEditorCardColSpan(item: OverdueCronLayoutEditorItem): boolean {
    const idx = this.COL_SPAN_STEPS.indexOf(this._normalizeColSpan(item.colSpan));
    return idx >= 0 && idx < this.COL_SPAN_STEPS.length - 1;
  }

  getEditorColSpanClass(span: number): string {
    switch (this._normalizeColSpan(span)) {
      case 4: return 'col-span-4';
      case 6: return 'col-span-6';
      case 8: return 'col-span-8';
      case 12: return 'col-span-12';
      default: return 'col-span-12';
    }
  }

  getEditorColSpanLabel(span: number): string {
    switch (this._normalizeColSpan(span)) {
      case 4: return 'Small';
      case 6: return 'Medium';
      case 8: return 'Large';
      case 12: return 'Full';
      default: return `${span}`;
    }
  }

  // ─── Editor: drag-drop reorder ─────────────────────────────────────────
  /**
   * Reorders within the editor list, but clamps the target index so the
   * dragged card never crosses a `group` boundary. The page renders two
   * separate grids (overview / analytics) split by the static date picker
   * control — letting a section jump groups in the popup would create a
   * mismatch between what the popup previews and what the live page
   * actually renders, so we forbid it.
   */
  onEditorCardDrop(event: CdkDragDrop<OverdueCronLayoutEditorItem[]>): void {
    const next = applyLayoutEditorCardDrop(this.editorItems(), event, {
      strategy: 'shift',
      getDropBounds: (items, from) => resolveLayoutEditorGroupDropBounds(items, from),
    });
    if (!next) return;
    this.editorItems.set(next);
  }

  // ─── Layout state private helpers ──────────────────────────────────────
  private _initLayout(): void {
    const stored = this._loadLayoutFromStorage();
    const layout: Record<OverdueCronSectionId, OverdueCronSectionLayoutItem> =
      {} as Record<OverdueCronSectionId, OverdueCronSectionLayoutItem>;

    this.SECTIONS.forEach((s, idx) => {
      const sid = s.id as OverdueCronSectionId;
      const storedItem = stored?.[sid];
      layout[sid] = {
        id: sid,
        visible: storedItem?.visible ?? true,
        colSpan: this._normalizeColSpan(
          storedItem?.colSpan ?? this.DEFAULT_COL_SPAN[sid] ?? 12,
        ),
        order: storedItem?.order ?? idx,
      };
    });

    this.layoutMap.set(layout);
  }

  private _loadLayoutFromStorage(): Record<string, OverdueCronSectionLayoutItem> | null {
    try {
      const raw = localStorage.getItem(this.LAYOUT_LS_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }

  private _saveLayoutToStorage(
    layout: Record<OverdueCronSectionId, OverdueCronSectionLayoutItem>,
  ): void {
    try {
      localStorage.setItem(this.LAYOUT_LS_KEY, JSON.stringify(layout));
    } catch {
      // localStorage unavailable (e.g. private mode) — silently ignore.
    }
  }

  private _normalizeColSpan(span: number | null | undefined): number {
    const allowed = [4, 6, 8, 12];
    if (span == null || !Number.isFinite(span)) return 12;
    if (allowed.includes(span)) return span;
    if (span <= 4) return 4;
    if (span <= 6) return 6;
    if (span <= 8) return 8;
    return 12;
  }

  /**
   * Reorders a layout/editor list when a card's visibility is toggled.
   * Clamps the move to the dragged card's group so visibility toggles
   * never push a section across the date-picker boundary.
   */
  private _reorderItemsOnVisibilityToggle(
    items: OverdueCronLayoutEditorItem[],
    id: OverdueCronSectionId,
  ): OverdueCronLayoutEditorItem[] {
    const idx = items.findIndex((i) => i.id === id);
    if (idx === -1) return items;

    const current = items[idx];
    const becomingHidden = current.visible;
    const updated = { ...current, visible: !current.visible };

    const without = items.filter((_, i) => i !== idx);

    let next: OverdueCronLayoutEditorItem[];
    if (becomingHidden) {
      const groupEnd = without
        .map((row, i) => ({ row, i }))
        .filter((entry) => entry.row.group === current.group)
        .reduce((acc, entry) => Math.max(acc, entry.i), -1);
      next = [
        ...without.slice(0, groupEnd + 1),
        updated,
        ...without.slice(groupEnd + 1),
      ];
    } else {
      const firstHiddenInGroup = without.findIndex(
        (i) => !i.visible && i.group === current.group,
      );
      if (firstHiddenInGroup === -1) {
        const groupEnd = without
          .map((row, i) => ({ row, i }))
          .filter((entry) => entry.row.group === current.group)
          .reduce((acc, entry) => Math.max(acc, entry.i), -1);
        next = [
          ...without.slice(0, groupEnd + 1),
          updated,
          ...without.slice(groupEnd + 1),
        ];
      } else {
        next = [
          ...without.slice(0, firstHiddenInGroup),
          updated,
          ...without.slice(firstHiddenInGroup),
        ];
      }
    }

    return next.map((row, order) => ({ ...row, order }));
  }
}
